import { h } from '@omni-torn/ui';
import { buildPolicyWithRequirements, ocKey, readRequirementsDoc, seatThresholds, seatWeight, type OcLevel, type OcPolicy, type SeatThresholds } from '@omni-torn/shared';
import { FACTION_HOF_CATEGORIES, HOF_CATEGORIES, HOF_PAGE, type RefAward, type RefItem, type TornRefAnswer, type TornRefKind } from '@omni-torn/shared/torn-ref';
import { bankEarnings, BANK_MAX } from '@omni-torn/shared/torn-sims';
import { server } from '../session';
import { fromTorn, oops, ref } from '../torn-lists';
import { factionsWarsBody, profileLink } from './ranked-wars';
import { ago, box, duration, ext, kids, loading, money, num, problem, profileUrl, signed, table, tabs, tct } from '../ui';

/**
 * The website's "Torn" pages: Torn's own public lists (items, bank, city shops, honors and medals, Hall of Fame, top
 * factions and ranked wars, rackets and territory wars, organized crimes), read by the Omni-Torn server with its own
 * key and kept there (apps/api/src/torn-data.ts) — so opening these pages costs the player's key nothing.
 */

// ---------------------------------------------------------------- data

export { ref };

function page(view: HTMLElement, title: string, what: string): HTMLElement {
  const body = h('div', {}, loading(`Reading ${what}…`));
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, title)), body);
  return body;
}

const factionLink = (id: number | null, name?: string | null) => (id ? ext(`https://www.torn.com/factions.php?step=profile&ID=${id}`, name || `Faction ${id}`) : h('span', { class: 'muted' }, 'nobody'));
const lower = (s: string) => s.toLowerCase();

function searchBox(placeholder: string, onInput: (q: string) => void): HTMLInputElement {
  const box = h('input', { type: 'search', placeholder, class: 'filter', 'aria-label': placeholder }) as HTMLInputElement;
  box.addEventListener('input', () => onInput(lower(box.value.trim())));
  return box;
}

function select(label: string, options: Array<[string, string]>, value: string, onChange: (v: string) => void): HTMLSelectElement {
  const s = h('select', { 'aria-label': label }, ...options.map(([v, t]) => h('option', { value: v, ...(v === value ? { selected: true } : {}) }, t))) as HTMLSelectElement;
  s.addEventListener('change', () => onChange(s.value));
  return s;
}

// ---------------------------------------------------------------- items

export async function renderItems(view: HTMLElement): Promise<void> {
  const body = page(view, 'Items', 'Torn’s items');
  let a: TornRefAnswer<'items'>;
  try {
    a = await ref('items');
  } catch (err) {
    body.replaceChildren(oops(err, () => renderItems(view)));
    return;
  }
  const items = a.data;
  const types = [...new Set(items.map((i) => i.type))].sort();
  let q = '';
  let type = '';
  const out = h('div', {});
  const count = h('span', { class: 'muted small', role: 'status' });
  const draw = () => {
    const list = items.filter((i) => (!type || i.type === type) && (!q || lower(i.name).includes(q) || String(i.id) === q));
    count.textContent = `${num(list.length)} of ${num(items.length)}`;
    out.replaceChildren(
      table(list, [
        { label: 'Item', get: (i) => itemName(i), sort: (i) => i.name },
        { label: 'Market value', get: (i) => money(i.marketValue), sort: (i) => i.marketValue ?? 0, cls: 'r' },
        { label: 'Type', get: (i) => (i.subType ? `${i.type} · ${i.subType}` : i.type), sort: (i) => i.type },
        { label: 'Shop price', get: (i) => money(i.buyPrice), sort: (i) => i.buyPrice ?? 0, cls: 'r' },
        { label: 'Sells for', get: (i) => money(i.sellPrice), sort: (i) => i.sellPrice ?? 0, cls: 'r' },
        { label: 'Circulation', get: (i) => num(i.circulation), sort: (i) => i.circulation ?? 0, cls: 'r' },
      ], { initial: 1, remember: 'items', empty: 'No item matches.' }),
    );
  };
  draw();
  body.replaceChildren(
    box('All items', [
      h(
        'div',
        { class: 'toolbar' },
        searchBox('Search items (name or id)…', (x) => ((q = x), draw())),
        select('Category', [['', 'Every category'], ...types.map((t): [string, string] => [t, t])], '', (v) => ((type = v), draw())),
        count,
      ),
      out,
      fromTorn(a),
    ]),
  );
}

function itemName(i: RefItem): HTMLElement {
  return h(
    'span',
    { class: 'item-name', title: [i.description, i.effect, i.requirement].filter(Boolean).join('\n') },
    ext(`https://www.torn.com/page.php?sid=ItemMarket#/market/view=search&itemID=${i.id}`, i.name),
    h('span', { class: 'muted small' }, ` [${i.id}]`),
    i.tradable === false ? h('span', { class: 'muted small' }, ' · untradable') : null,
    i.vendor ? h('div', { class: 'muted small' }, i.vendor) : null,
  );
}

// ---------------------------------------------------------------- bank & shops

export async function renderBank(view: HTMLElement): Promise<void> {
  const body = page(view, 'Bank & shops', 'Torn’s bank rates and city shops');
  const [bank, shops, items] = await Promise.all([ref('bank').catch((e: Error) => e), ref('cityshops').catch((e: Error) => e), ref('items').catch(() => null)]);
  const value = new Map((items?.data ?? []).map((i) => [i.id, i.marketValue]));
  const again = () => renderBank(view);
  body.replaceChildren(bank instanceof Error ? oops(bank, again) : bankBox(bank), shops instanceof Error ? oops(shops, again) : shopsBox(shops, value, items));
}

function bankBox(a: TornRefAnswer<'bank'>): HTMLElement {
  const amount = h('input', { type: 'text', inputmode: 'numeric', value: '2,000,000,000', 'aria-label': 'Amount to invest', class: 'num-input' }) as HTMLInputElement;
  let merits = 0;
  let tci = false;
  const meritSel = select('Bank Interest merits', Array.from({ length: 11 }, (_, i): [string, string] => [String(i), `${i} merits (+${i * 5}%)`]), '0', (v) => ((merits = Number(v)), draw()));
  const tciBox = h('input', { type: 'checkbox', id: 'bank-tci' }) as HTMLInputElement;
  tciBox.addEventListener('change', () => ((tci = tciBox.checked), draw()));
  const out = h('div', {});
  const draw = () => {
    const amt = Math.min(BANK_MAX, Number(amount.value.replace(/[^\d.]/g, '')) || 0);
    out.replaceChildren(
      table(a.data, [
        { label: 'Period', get: (b) => b.label, sort: (b) => b.days },
        { label: 'Torn’s rate', get: (b) => `${num(b.rate, 2)}%`, sort: (b) => b.rate, cls: 'r' },
        { label: 'Your rate', get: (b) => `${num(bankEarnings(amt, b.rate, b.days, { merits, tci }).rate, 2)}%`, cls: 'r' },
        { label: 'You earn', get: (b) => money(bankEarnings(amt, b.rate, b.days, { merits, tci }).total), sort: (b) => bankEarnings(amt, b.rate, b.days, { merits, tci }).total, cls: 'r' },
        { label: 'A day', get: (b) => money(bankEarnings(amt, b.rate, b.days, { merits, tci }).perDay), sort: (b) => bankEarnings(amt, b.rate, b.days, { merits, tci }).perDay, cls: 'r' },
      ], { initial: 0, desc: false }),
    );
  };
  amount.addEventListener('input', draw);
  draw();
  return box('City bank', [
    h('div', { class: 'toolbar' }, h('label', {}, 'Invest ', amount), meritSel, h('label', { class: 'check' }, tciBox, ' TCI company perk (+10%)')),
    out,
    h('p', { class: 'muted small' }, 'Torn’s rates are yearly, as its bank page shows them; earnings are worked out the same way (at most $2b invested). Rates change every day.'),
    fromTorn(a),
  ], { id: 'bank' });
}

function shopsBox(a: TornRefAnswer<'cityshops'>, value: Map<number, number | null>, items: TornRefAnswer<'items'> | null): HTMLElement {
  const rows = a.data.flatMap((s) => s.items.map((i) => ({ shop: s.name, ...i, value: value.get(i.id) ?? null })));
  let q = '';
  let shop = '';
  const out = h('div', {});
  const draw = () =>
    out.replaceChildren(
      table(
        rows.filter((r) => (!shop || r.shop === shop) && (!q || lower(r.name).includes(q))),
        [
          { label: 'Item', get: (r) => h('span', {}, r.name, h('span', { class: 'muted small' }, ` ${r.type}`)), sort: (r) => r.name },
          { label: 'Shop', get: (r) => r.shop, sort: (r) => r.shop },
          { label: 'Price', get: (r) => money(r.price), sort: (r) => r.price, cls: 'r' },
          { label: 'In stock', get: (r) => num(r.stock), sort: (r) => r.stock ?? 0, cls: 'r' },
          { label: 'Market value', get: (r) => money(r.value), sort: (r) => r.value ?? 0, cls: 'r' },
          { label: 'Profit each', get: (r) => (r.value ? h('span', { class: r.value > r.price ? 't-green' : 't-red' }, signed(r.value - r.price, money)) : '—'), sort: (r) => (r.value ? r.value - r.price : -Infinity), cls: 'r' },
        ],
        { initial: 5, remember: 'shops', empty: 'Nothing here.' },
      ),
    );
  draw();
  return box('City shops', [
    h('div', { class: 'toolbar' }, searchBox('Search shop items…', (x) => ((q = x), draw())), select('Shop', [['', 'Every shop'], ...a.data.map((s): [string, string] => [s.name, s.name])], '', (v) => ((shop = v), draw()))),
    out,
    fromTorn(a, items),
  ], { id: 'shops' });
}

// ---------------------------------------------------------------- honors & medals

export async function renderHonors(view: HTMLElement): Promise<void> {
  const body = page(view, 'Honors & medals', 'Torn’s honors and medals');
  const [honors, medals] = await Promise.all([ref('honors').catch((e: Error) => e), ref('medals').catch((e: Error) => e)]);
  let which: 'honors' | 'medals' = 'honors';
  let q = '';
  let type = '';
  const out = h('div', {});
  const typeSlot = h('span', {});
  const draw = () => {
    const a = which === 'honors' ? honors : medals;
    if (a instanceof Error) {
      typeSlot.replaceChildren();
      out.replaceChildren(oops(a, () => renderHonors(view)));
      return;
    }
    const types = [...new Set(a.data.map((x) => x.type))].sort();
    if (type && !types.includes(type)) type = '';
    typeSlot.replaceChildren(select('Type', [['', 'Every type'], ...types.map((t): [string, string] => [t, t])], type, (v) => ((type = v), draw())));
    const list = a.data.filter((x) => (!type || x.type === type) && (!q || lower(`${x.name} ${x.description}`).includes(q)));
    out.replaceChildren(
      table<RefAward>(list, [
        { label: 'Name', get: (x) => h('strong', {}, x.name), sort: (x) => x.name },
        { label: 'How to get it', get: (x) => x.description, sort: (x) => x.description },
        { label: 'Type', get: (x) => x.type, sort: (x) => x.type },
        { label: 'Rarity', get: (x) => x.rarity ?? '—', sort: (x) => x.circulation ?? 0 },
        { label: 'Players with it', get: (x) => num(x.circulation), sort: (x) => x.circulation ?? 0, cls: 'r' },
      ], { initial: 4, desc: false, empty: 'Nothing matches.' }),
      fromTorn(a),
    );
  };
  body.replaceChildren(
    box('Awards', [
      tabs([{ id: 'honors', label: 'Honors' }, { id: 'medals', label: 'Medals' }], which, (id) => ((which = id as typeof which), draw())),
      h('div', { class: 'toolbar' }, searchBox('Search names and descriptions…', (x) => ((q = x), draw())), typeSlot),
      out,
    ]),
  );
  draw();
}

// ---------------------------------------------------------------- hall of fame

export async function renderHof(view: HTMLElement): Promise<void> {
  view.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Hall of Fame')));
  let cat = 'level';
  let offset = 0;
  const out = h('div', {}, loading('Reading the Hall of Fame…'));
  const pager = h('span', {});
  let drawing = 0;
  const draw = async () => {
    const mine = ++drawing;
    out.replaceChildren(loading('Reading the Hall of Fame…'));
    const c = HOF_CATEGORIES.find((x) => x.id === cat)!;
    try {
      const a = await ref('hof', `${cat}:${offset}`);
      if (mine !== drawing) return;
      out.replaceChildren(
        table(a.data, [
          { label: '#', get: (r) => num(r.position), sort: (r) => r.position, cls: 'r' },
          { label: 'Player', get: (r) => h('span', {}, ext(profileUrl(r.id), r.name), h('span', { class: 'muted small' }, ` [${r.id}]`)), sort: (r) => r.name },
          { label: 'Level', get: (r) => num(r.level), sort: (r) => r.level ?? 0, cls: 'r' },
          { label: 'Faction', get: (r) => (r.factionId ? factionLink(r.factionId) : '—') },
          { label: c.label, get: (r) => hofValue(c, r.value), sort: (r) => r.value, cls: 'r' },
        ], { initial: 0, desc: false }),
        fromTorn(a),
      );
    } catch (err) {
      if (mine === drawing) out.replaceChildren(oops(err, draw));
    }
  };
  const pageSel = () =>
    select('Places', Array.from({ length: 10 }, (_, i): [string, string] => [String(i * HOF_PAGE), `${num(i * HOF_PAGE + 1)}–${num((i + 1) * HOF_PAGE)}`]), String(offset), (v) => ((offset = Number(v)), void draw()));
  pager.replaceChildren(pageSel());
  view.append(
    box('Boards', [
      h('div', { class: 'toolbar' }, select('Board', HOF_CATEGORIES.map((c): [string, string] => [c.id, c.label]), cat, (v) => ((cat = v), (offset = 0), pager.replaceChildren(pageSel()), void draw())), pager),
      out,
      h('p', { class: 'muted small' }, 'Battle stats boards are left out: Omni-Torn never shows battle stats.'),
    ], { id: 'hof' }),
    playerHofBox(),
  );
  await draw();
}

function hofValue(c: (typeof HOF_CATEGORIES)[number], v: number): string {
  if (c.money) return money(v);
  if (c.time) return duration(v);
  return num(v);
}

function playerHofBox(): HTMLElement {
  const input = h('input', { type: 'search', inputmode: 'numeric', placeholder: 'Player id', class: 'filter', 'aria-label': 'Player id' }) as HTMLInputElement;
  const go = h('button', { type: 'submit', class: 'btn-small' }, 'Show');
  const out = h('div', {});
  const form = h('form', { class: 'toolbar' }, input, go);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = input.value.replace(/\D/g, '');
    if (!id) return;
    out.replaceChildren(loading('Asking Torn…'));
    try {
      const a = await ref('playerhof', id);
      const rows = Object.entries(a.data).map(([k, v]) => ({ k, ...v, c: HOF_CATEGORIES.find((c) => c.id === k) }));
      out.replaceChildren(
        h('p', {}, ext(profileUrl(Number(id)), `Player [${id}]`), ' in the Hall of Fame:'),
        table(rows, [
          { label: 'Board', get: (r) => r.c?.label ?? r.k, sort: (r) => r.c?.label ?? r.k },
          { label: 'Value', get: (r) => (r.c ? hofValue(r.c, r.value) : num(r.value)), cls: 'r' },
          { label: 'Place', get: (r) => `#${num(r.rank)}`, sort: (r) => r.rank, cls: 'r' },
        ], { initial: 2, desc: false, empty: 'Not on any board.' }),
        fromTorn(a),
      );
    } catch (err) {
      out.replaceChildren(problem(err));
    }
  });
  return box('A player’s places', [form, out], { id: 'player-hof' });
}

// ---------------------------------------------------------------- factions & wars


/** The Factions page's lists (pages/factions.ts draws the page): top factions by respect, chain or rank, and the ranked wars. */
export async function appendFactionLists(view: HTMLElement, again: () => unknown): Promise<void> {
  let cat = 'respect';
  const out = h('div', {}, loading('Reading the top factions…'));
  const draw = async () => {
    out.replaceChildren(loading('Reading the top factions…'));
    const label = FACTION_HOF_CATEGORIES.find((c) => c.id === cat)!.label;
    try {
      const a = await ref('factionhof', cat);
      out.replaceChildren(
        table(a.data, [
          { label: '#', get: (r) => num(r.position), sort: (r) => r.position, cls: 'r' },
          { label: 'Faction', get: (r) => profileLink(r.id, r.name), sort: (r) => r.name },
          { label: 'Members', get: (r) => num(r.members), sort: (r) => r.members ?? 0, cls: 'r' },
          { label: 'Rank', get: (r) => r.rank ?? '—', sort: (r) => r.rank ?? '' },
          { label, get: (r) => num(r.value), sort: (r) => r.value, cls: 'r' },
        ], { initial: 0, desc: false }),
        fromTorn(a),
      );
    } catch (err) {
      out.replaceChildren(oops(err, draw));
    }
  };
  const wars = h('div', {}, loading('Reading the ranked wars…'));
  view.append(box('Top factions', [tabs(FACTION_HOF_CATEGORIES.map((c) => ({ id: c.id, label: c.label })), cat, (id) => ((cat = id), void draw())), out], { id: 'top-factions' }), box('Ranked wars', wars, { id: 'ranked-wars' }));
  await Promise.all([
    draw(),
    ref('rankedwars')
      .then((a) => wars.replaceChildren(...factionsWarsBody(a)))
      .catch((err) => wars.replaceChildren(oops(err, again))),
  ]);
}

// ---------------------------------------------------------------- territory & rackets

export async function renderTerritory(view: HTMLElement): Promise<void> {
  const body = page(view, 'Territory & rackets', 'rackets and territory wars');
  const [rackets, wars] = await Promise.all([ref('rackets').catch((e: Error) => e), ref('territorywars').catch((e: Error) => e)]);
  const now = Date.now() / 1000;
  body.replaceChildren(
    box(
      'Rackets',
      rackets instanceof Error
        ? oops(rackets, () => renderTerritory(view))
        : [
            table(rackets.data, [
              { label: 'Territory', get: (r) => ext(`https://www.torn.com/city.php#terrName=${r.territory}`, r.territory), sort: (r) => r.territory },
              { label: 'Racket', get: (r) => h('strong', {}, r.name), sort: (r) => r.name },
              { label: 'Level', get: (r) => num(r.level), sort: (r) => r.level ?? 0, cls: 'r' },
              { label: 'Gives', get: (r) => r.reward, sort: (r) => r.reward },
              { label: 'Held by', get: (r) => factionLink(r.factionId), sort: (r) => r.factionId ?? 0 },
              { label: 'Since', get: (r) => (r.changed ? ago(r.changed * 1000) : '—'), sort: (r) => r.changed ?? 0, cls: 'r' },
            ], { initial: 2, empty: 'No rackets.' }),
            fromTorn(rackets),
          ],
      { id: 'rackets' },
    ),
    box(
      'Territory wars',
      wars instanceof Error
        ? oops(wars, () => renderTerritory(view))
        : [
            table(wars.data, [
              { label: 'Territory', get: (w) => w.territory, sort: (w) => w.territory },
              { label: 'Attacking', get: (w) => factionLink(w.assaulting), sort: (w) => w.assaulting ?? 0 },
              { label: 'Defending', get: (w) => factionLink(w.defending), sort: (w) => w.defending ?? 0 },
              { label: 'Score', get: (w) => (w.required ? `${num(w.score)} / ${num(w.required)}` : num(w.score)), sort: (w) => (w.required ? (w.score ?? 0) / w.required : 0), cls: 'r' },
              { label: 'Ends', get: (w) => (w.ends ? (w.ends > now ? `in ${duration(w.ends - now)}` : 'ended') : '—'), sort: (w) => w.ends ?? 0, cls: 'r' },
            ], { initial: 4, desc: false, empty: 'No territory wars right now.' }),
            fromTorn(wars),
          ],
      { id: 'territory-wars' },
    ),
  );
}

// ---------------------------------------------------------------- organized crimes

/** Where a seat's pass rate comes from, in words. */
const FROM_WORDS: Record<OcLevel, string> = {
  seat: 'Omni-Torn’s built-in OC guidelines',
  'role-in-crime': 'Omni-Torn’s built-in OC guidelines',
  role: 'Omni-Torn’s built-in OC guidelines',
  crime: 'Omni-Torn’s built-in OC guidelines',
  level: 'Omni-Torn’s built-in OC guidelines (by crime level)',
  derived: 'worked out from tornprobability’s success model, matched to the guidelines’ chance of success',
  general: 'graded by how much the role matters',
  none: 'no rule',
};

/** "needs 68%+ (aim 73%)" for a seat, by the built-in guidelines, then the derived requirements (GET /api/oc/requirements). */
function seatNeed(th: SeatThresholds): HTMLElement | null {
  if (th.min === null && th.target === null) return null;
  const words = th.min === 0 ? 'anyone' : `needs ${num(th.min)}%+`;
  return h(
    'span',
    { class: 'oc-need', 'data-oc-need': th.minFrom, title: `Pass rate: ${FROM_WORDS[th.minFrom]}. Your faction’s own rules, if it published some, come first in the extension.` },
    ` · ${words}`,
    th.target !== null && th.target !== th.min ? h('span', { class: 'muted' }, ` (aim ${num(th.target)}%)`) : null,
  );
}

/** The requirements every Omni-Torn user starts from (built-in guidelines > derived > graded); null when the server has none. */
async function defaultPolicy(): Promise<OcPolicy | null> {
  try {
    const r = await server<{ requirements?: unknown }>('GET', '/api/oc/requirements');
    return buildPolicyWithRequirements({ requirements: readRequirementsDoc(r.requirements) }).policy;
  } catch {
    return null;
  }
}

export async function renderCrimes(view: HTMLElement): Promise<void> {
  const body = page(view, 'Organized crimes', 'Torn’s organized crimes');
  let a: TornRefAnswer<'organizedcrimes'>;
  let policy: OcPolicy | null;
  try {
    [a, policy] = await Promise.all([ref('organizedcrimes'), defaultPolicy()]);
  } catch (err) {
    body.replaceChildren(oops(err, () => renderCrimes(view)));
    return;
  }
  const levels = [...new Set(a.data.map((c) => c.difficulty ?? 0))].sort((x, y) => x - y);
  body.replaceChildren(
    ...kids(...levels.map((lvl) =>
      box(
        lvl ? `Level ${lvl}` : 'Other',
        a.data
          .filter((c) => (c.difficulty ?? 0) === lvl)
          .map((c) => {
            const w = a.weights?.[ocKey(c.name)];
            const ths = c.slots.map((s) => (policy ? seatThresholds(policy, c.name, s.name, w, null, c.difficulty) : null));
            const derived = ths.find((t) => t?.derived)?.derived ?? null;
            return h(
              'div',
              { class: 'oc-scenario', 'data-oc': c.name },
              h('h3', { class: 'sub' }, c.name, c.spawn ? h('span', { class: 'muted small' }, ` · ${c.spawn}`) : null),
              c.description ? h('p', { class: 'muted small' }, c.description) : null,
              derived ? h('p', { class: 'muted small', 'data-oc-derived': '' }, `Pass rates worked out from tornprobability’s success model: about ${num(derived.minSuccess * 100)}% chance of success with every seat at its minimum, ${num(derived.targetSuccess * 100)}% at the aims${derived.relaxed ? ' (as close as this crime gets)' : ''}.`) : null,
              h(
                'ul',
                { class: 'plain-list oc-roles' },
                ...c.slots.map((s, i) => {
                  const weight = seatWeight(w, s.name);
                  const th = ths[i];
                  return h(
                    'li',
                    {},
                    h('strong', {}, s.name),
                    weight !== null ? h('span', { class: 'oc-weight', title: 'How much this role matters to the crime’s success (tornprobability’s weights, learned further from finished crimes)' }, ` ${num(weight, 1)}%`) : null,
                    th ? seatNeed(th) : null,
                    s.item ? h('span', { class: 'muted' }, ` — needs ${s.item}${s.itemUsed === true ? ' (used up)' : s.itemUsed === false ? ' (kept)' : ''}`) : null,
                  );
                }),
              ),
            );
          }),
      ),
    ),
    policy ? h('p', { class: 'muted small' }, 'Green: how much each role matters. "Needs": the pass rate (CPR) Omni-Torn asks for each seat by default — its built-in OC guidelines, and for crimes they don’t number, requirements worked out from tornprobability’s success model to match the guidelines’ chance of success. In the extension, your faction’s published rules and your own come first.') : null,
    a.data.length ? fromTorn(a) : h('p', { class: 'muted' }, 'Torn listed no crimes.')),
  );
}

